"use client";

import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { BarcodeScanner } from "react-barcode-scanner";

import { addOrIncrementCartItem, normalizeScanCode, resolveSingleProductFromScan } from "../../core/scanToCart.core";
import { useTranslation } from "../../core/translation.core";
import useCart from "../../hooks/services/useCart.hook";
import useRetailShop from "../../hooks/useRetailShop";
import { Cart } from "../../types/cart";
import HerIcon from "../../icons/her.icon";
import BackPosIcon from "../../icons/posicons/back.icon";
import PosCartIcon from "../../icons/posicons/cart.icon";
import PosHomeIcon from "../../icons/posicons/home.icon";
import ScanIcon from "../../icons/posicons/scan.icon";
import SearchIcon from "../../icons/posicons/search.icon";
import CartNumber from "../orderProductPanel/cartNumber.comp";
import ApperanceToggleComp from "../reuseables/apperanceToggle.comp";
import { PosModal } from "../reuseables/modal.comp";
import SearchProductPanel from "../reuseables/searchPanel.comp";
import MobilePosNav from "./mobilePosNav.comp";
import { useCookies } from "../../hooks/useCookies.hook";
import ClientLogo from "./clientLogo.comp";

interface IProps {
  showback?: boolean;
}

export default function PosHeader({ showback }: IProps) {
  return (
    <div className="fixed top-0 w-full bg-white z-[50]">
      <NavDesktop />
      <NavMobile showback={showback} />
    </div>
  );
}

function NavDesktop() {
  const isClientLogo = useCookies("isClientLogo");
  const pos = useRetailShop();

  const navStyles =
    "hidden lg:flex px-4 " + //
    "border-b-none lg:border-b-[1px] " +
    "dark:border-none bg-white dark:bg-[#272B34] " +
    "py-1 lg:py-3 px-4 " +
    "flex justify-between items-center ";

  if (!pos) return null;

  return (
    <nav id="nav-desktop" className={navStyles}>
      <section className="flex items-center gap-12">
        <Link href="/" className={`${isClientLogo ? " hidden" : " block"}`}>
          <HerIcon className="w-24 md:w-28" />
        </Link>

        <SearchProductPanel />
      </section>

      <section className="flex gap-4 items-center">
        {isClientLogo ? <ClientLogo /> : null}
        <h1 className="dark:text-white text-sm">{pos?.name}</h1>

        <ApperanceToggleComp />
        <PosCart />
        <ScanProduct />
      </section>
    </nav>
  );
}

function NavMobile({ showback }: IProps) {
  const pos = useRetailShop();
  const router = useRouter();
  const [showNav, setShowNav] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const toggleNav = () => setShowNav((prev) => !prev);
  const toggleSearch = () => setShowSearch((prev) => !prev);

  return (
    <nav id="nav-mobile" className="relative py-3 dark:bg-her-darkblue2 bg-white lg:hidden">
      <div className="relative flex justify-between items-center px-4">
        <div className="flex gap-2 items-center">
          <Link href="/">
            <HerIcon className="w-24 md:w-28" />
          </Link>
          <MobileShowBack showback={showback} toggleNav={toggleNav} />
        </div>

        <div className="flex gap-2 items-center">
          <h1 className="hidden sm:block dark:text-white text-sm">{pos?.name}</h1>
          <ApperanceToggleComp />
          <button onClick={toggleSearch} className="bg-her-lightgray p-2 rounded-lg">
            <SearchIcon />
          </button>
          <button onClick={() => router.push("/?cartOpen=true")}>
            <PosCart />
          </button>
          <ScanProduct isMobile />
        </div>
      </div>

      {showNav ? (
        <div className="w-full h-[91vh] fixed bottom-0 bg-white dark:bg-her-darkblue2 pt-10">
          <MobilePosNav className="w-full" toggleShowNav={toggleNav} />
        </div>
      ) : null}
      {showSearch ? (
        <div className="border-t-[0.5px] w-full flex justify-center pt-3 mt-3 bg-white">
          <SearchProductPanel />
        </div>
      ) : null}
    </nav>
  );
}

function PosCart() {
  const router = useRouter();

  return (
    <>
      <section onClick={() => router.push("/")} className="p-1 bg-surface rounded-full aspect-square relative">
        <PosCartIcon />
        <div className="absolute top-0 -right-1">
          <CartNumber />
        </div>
      </section>
    </>
  );
}

function ScanProduct({ isMobile }: { isMobile?: boolean }) {
  const { msg, t } = useTranslation();
  const [startScanner, setStartScanner] = useState(false);

  const scanbtnStyles = "bg-blue-500 text-white flex gap-2 items-center py-2 px-2 lg:px-4 rounded-md cursor-pointer";

  return (
    <section>
      {startScanner ? <ScannerModal setStartScanner={setStartScanner} /> : null}
      {isMobile ? (
        <div onClick={() => setStartScanner(true)} className={scanbtnStyles}>
          <ScanIcon />
        </div>
      ) : (
        <div onClick={() => setStartScanner(true)} className={scanbtnStyles}>
          <ScanIcon />
          <h2>{t(msg.posMenuItems.scanProduct)}</h2>
        </div>
      )}
    </section>
  );
}

function MobileShowBack({ showback, toggleNav }: { showback?: boolean; toggleNav: VoidFunction }) {
  const router = useRouter();

  if (showback) {
    return (
      <button onClick={() => router.back()}>
        <BackPosIcon />
      </button>
    );
  }

  return (
    <button onClick={toggleNav}>
      <PosHomeIcon className="dark:text-white" />
    </button>
  );
}

const ScannerModal = ({ setStartScanner }: { setStartScanner: Dispatch<SetStateAction<boolean>> }) => {
  const { push, pathname, locale } = useRouter();
  const queryClient = useQueryClient();
  const btnRef = useRef(null);
  const { addItem, updateItem, getProductWarehouseId } = useCart();

  const handleFind = async (code: string) => {
    const normalized = normalizeScanCode(code);
    const product = await resolveSingleProductFromScan(normalized);
    if (!product) return;

    if (pathname === "/") {
      const cart = queryClient.getQueryData<Cart>(["PosCart", locale]);
      addOrIncrementCartItem({
        product,
        cart,
        addItem,
        updateItem,
        getProductWarehouseId,
        queryClient,
      });
    } else {
      push("/product/" + product.id);
    }
  };

  return (
    //  @ts-ignore
    <PosModal setModalOpened={setStartScanner} btnRef={btnRef}>
      <BarcodeScanner
        onCapture={(barcode) => {
          setStartScanner(false);
          handleFind(barcode.rawValue);
        }}
        options={{
          formats: ["codabar", "qr_code", "ean_13", "ean_8", "itf", "unknown"],
        }}
      />
    </PosModal>
  );
};
